import { CSSProperties } from "react";
import {
  BrandTheme,
  StyleConfig,
  ThemeConfig,
  QuestThemeData,
} from "../types/Challenges.type";
import { hexToGlassColor } from "../../helphub/component/chatcomponents/helperfunction";
import { extractGradientColors } from "../../../../general";

export class ThemeManager {
  private brandTheme: BrandTheme;
  private styleConfig: StyleConfig;
  private themeConfig: ThemeConfig;
  private questThemeData: QuestThemeData;

  constructor(
    brandTheme: BrandTheme = {},
    styleConfig: StyleConfig = {},
    themeConfig: ThemeConfig = {},
    questThemeData: QuestThemeData = {}
  ) {
    this.brandTheme = brandTheme;
    this.styleConfig = styleConfig;
    this.themeConfig = themeConfig;
    this.questThemeData = questThemeData;
  }

  getMainHeadingStyles(): CSSProperties {
    return {
      color:
        this.styleConfig?.MainHeading?.color || "white",
      ...this.styleConfig?.MainHeading,
    };
  }

  getHeadingStyles(): CSSProperties {
    return {
      color: this.styleConfig?.Heading?.color || this.brandTheme?.titleColor || this.themeConfig?.primaryColor,
      // ...this.styleConfig?.Heading,
    };
  }

  getPointsColorStyles(progressbarPercent: number): CSSProperties {
    return {
      color: progressbarPercent
        ? this.styleConfig?.PointsColor?.color || this.brandTheme?.contentColor || "#9035FF"
        : this.brandTheme?.contentColor,
    };
  }

  getProgressBarColorStyles(): string {
    return this.styleConfig?.ProgressBarColor?.background || this.brandTheme?.primaryColor || this.questThemeData?.accentColor || "#9035FF";
  }

  getSearchStyles(): CSSProperties {
    return {
      background: this.styleConfig?.Search?.background
        ? this.styleConfig?.Search?.background
        : "",
      color: this.styleConfig?.Search?.color
        ? this.styleConfig?.Search?.color
        : "",
      borderRadius: this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || '12px',
    };
  }

  getSearchColorStyles(): CSSProperties {
    return {
      color: this.styleConfig?.Search?.color
        ? this.styleConfig?.Search?.color
        : "",
    };
  }

  getFormStyles(): CSSProperties {
    return {
      background:
        this.styleConfig?.Form?.backgroundColor || !(this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? 'linear-gradient(13deg, #fff 38.24%, #9035ff 59.39%, #0065ff 70.7%)' : (this.brandTheme?.primaryColor || this.questThemeData?.accentColor)?.includes("gradient") ? (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) :
          `linear-gradient(14deg, rgba(255, 255, 255, 0) 38.24%, ${hexToGlassColor(this.brandTheme?.primaryColor || this.questThemeData?.accentColor, 0.6)} 59.39%, ${this.brandTheme?.primaryColor || this.questThemeData?.accentColor} 70.7%)`,
      fontFamily:
        this.styleConfig?.Form?.fontFamily || this.themeConfig.fontFamily,
      borderRadius: this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || '12px',
      ...this.styleConfig?.Form,
    };
  }

  getChallengesCardStyles(): CSSProperties {
    return {
      background:
        this.styleConfig?.InnerBackground?.background ||
        this.themeConfig?.backgroundColor,
      borderRadius: this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || '12px',
      // this.brandTheme?.background ||
      // this.questThemeData?.InnerBackground?.background,
    };
  }

  getLabelStyles(): CSSProperties {
    return {
      color: this.styleConfig?.Label?.color || this.themeConfig?.primaryColor,
      ...this.styleConfig?.Label,
    };
  }

  getIconStyles(): string {
    return extractGradientColors(this.styleConfig?.IconStyle?.color || this.brandTheme?.primaryColor || this.questThemeData?.accentColor) || "#9035FF";
  }

  getFooterStyles(): CSSProperties {
    return {
      background:
        this.styleConfig?.Footer?.FooterStyle?.backgroundColor ||
        this.styleConfig?.Form?.backgroundColor ||
        this.styleConfig?.Form?.background ||
        this.themeConfig?.backgroundColor || "white",
      borderRadius: `0px 0px ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"} ${this.questThemeData?.borderRadius || this.brandTheme?.borderRadius || "12px"}` || '12px',
      ...this.styleConfig?.Footer?.FooterStyle,
    };
  }

  getFooterIconStyles(): CSSProperties {
    return this.styleConfig?.Footer?.FooterIcon || {};
  }

  getFooterLinkStyles(): string {
    return this.styleConfig?.Footer?.FooterLink || "";
  }

  getFooterTextStyle(): CSSProperties {
    return this.styleConfig?.Footer?.FooterTextStyle || {};
  }

  getFooterText(): string {
    return this.styleConfig?.Footer?.FooterText || "";
  }

  getChallengesSvgColors() {
    return {
      primary: (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? generateBadgeColors(this.brandTheme?.primaryColor || this.questThemeData?.accentColor)?.dark : "#6525B3",
      secondary: (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? generateBadgeColors(this.brandTheme?.primaryColor || this.questThemeData?.accentColor)?.base : "#BF8AFF",
      tertiary: (this.brandTheme?.primaryColor || this.questThemeData?.accentColor) ? generateBadgeColors(this.brandTheme?.primaryColor || this.questThemeData?.accentColor)?.light : "#A357FF",
    }
  }
}

export function toGlassColor(color, opacity = 0.05) {
  if (!color) return `rgba(0, 0, 0, ${opacity})`;

  if (color.includes('gradient')) {
    color = extractGradientColors(color);
  }

  if (color.startsWith('rgb')) {
    return color.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
  }

  if (!color.startsWith('#') && !color.startsWith('rgb')) {
    const tempEl = document.createElement('div');
    tempEl.style.color = color;
    document.body.appendChild(tempEl);

    const computedColor = getComputedStyle(tempEl).color;
    document.body.removeChild(tempEl);

    if (computedColor.startsWith('rgb')) {
      return computedColor.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
    }

    return `rgba(0, 0, 0, ${opacity})`;
  }

  let hex = color.replace('#', '');

  if (hex.length === 3) {
    hex = hex.split('').map(char => char + char).join('');
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

function generateBadgeColors(input: string) {
  const rgbToHex = (r: number, g: number, b: number) => (
    '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('').toUpperCase()
  );

  const parseRgbString = (rgb: string) => {
    const parts = rgb.match(/\d+/g);
    if (!parts || parts.length < 3) throw new Error("Invalid rgb format");
    return rgbToHex(+parts[0], +parts[1], +parts[2]);
  };

  const resolveColor = (color: string): string => {
    if (color.includes('gradient')) {
      color = extractGradientColors(color);
    }

    if (color.startsWith('rgb')) {
      return parseRgbString(color);
    }

    if (!color.startsWith('#')) {
      const tempEl = document.createElement('div');
      tempEl.style.color = color;
      document.body.appendChild(tempEl);
      const computed = getComputedStyle(tempEl).color;
      document.body.removeChild(tempEl);

      if (computed.startsWith('rgb')) {
        return parseRgbString(computed);
      } else {
        throw new Error(`Could not resolve color: ${color}`);
      }
    }

    return color;
  };

  const adjustColor = (hex: string, percent: number) => {
    const amt = Math.round(2.55 * percent);
    return '#' + hex.replace(/^#/, '').match(/.{2}/g)!.map(x => {
      const n = parseInt(x, 16) + amt;
      return Math.min(255, Math.max(0, n)).toString(16).padStart(2, '0');
    }).join('').toUpperCase();
  };

  const co = resolveColor(input);
  const base  = adjustColor(co, -10);
  const light = adjustColor(base, 20);
  const dark = adjustColor(base, -20);

  return { base, light, dark };
}